import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ApiHandler from "../../../Handler/ApiHandler";
import { formatDuration } from "../../../Common/DurationCommon";
import "./catalogue.css";

const formatPrice = (price) => {
  if (price === null || price === undefined) {
    return "-";
  }

  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 2,
  }).format(price);
};

function Catalogue() {
  const navigate = useNavigate();
  const location = useLocation();
  const [prestations, setPrestations] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [activeGroupId, setActiveGroupId] = React.useState("");

  React.useEffect(() => {
    ApiHandler.get({ route: "public_catalogue_prestations_list" }, (response) => {
      if (response.status >= 400) {
        setError("Impossible de charger les prestations.");
        setLoading(false);
        return;
      }

      setPrestations(response.data?.prestations || []);
      setLoading(false);
    });
  }, []);

  const groupMap = React.useMemo(() => {
    const map = {};
    prestations.forEach((item) => {
      const key = String(item.prestationGroupId ?? "none");
      if (!map[key]) {
        map[key] = {
          id: key,
          label: item.prestationGroupLabel || "Autres prestations",
          description: item.prestationGroupDescription || "",
          items: [],
        };
      }
      if (!map[key].description && item.prestationGroupDescription) {
        map[key].description = item.prestationGroupDescription;
      }
      map[key].items.push(item);
    });
    return map;
  }, [prestations]);

  const groups = React.useMemo(() => Object.values(groupMap), [groupMap]);
  const tabs = React.useMemo(() => groups, [groups]);
  const activeGroup = React.useMemo(
    () => tabs.find((group) => group.id === activeGroupId) || null,
    [tabs, activeGroupId]
  );

  React.useEffect(() => {
    const groupIdFromUrl = new URLSearchParams(location.search).get("groupId");
    if (groupIdFromUrl && tabs.length > 0) {
      const groupMatch = tabs.find((group) => group.id === groupIdFromUrl);
      if (groupMatch && groupMatch.id !== activeGroupId) {
        setActiveGroupId(groupMatch.id);
        return;
      }
    }

    if (!activeGroupId && tabs.length > 0) {
      setActiveGroupId(tabs[0].id);
      return;
    }

    if (
      activeGroupId &&
      !tabs.find((group) => group.id === activeGroupId) &&
      tabs.length > 0
    ) {
      setActiveGroupId(tabs[0].id);
    }
  }, [activeGroupId, tabs, location.search]);

  return (
    <section className="catalogue-page">
      <header className="catalogue-header">
        <h2>Toutes nos prestations</h2>
      </header>

      {loading && <p className="catalogue-state">Chargement...</p>}
      {!loading && error && <p className="catalogue-state">{error}</p>}
      {!loading && !error && prestations.length === 0 && (
        <p className="catalogue-state">Aucune prestation disponible.</p>
      )}

      {!loading && !error && prestations.length > 0 && (
        <>
          <div className="catalogue-tabs">
            {tabs.map((group) => (
              <button
                key={group.id}
                className={`catalogue-tab ${activeGroupId === group.id ? "is-active" : ""}`}
                type="button"
                onClick={() => {
                  setActiveGroupId(group.id);
                  navigate(`/public/catalogue?groupId=${encodeURIComponent(group.id)}`, { replace: true });
                }}
              >
                {group.label}
              </button>
            ))}
          </div>
          {!!activeGroup?.description && (
            <section className="catalogue-group-panel">
              <p className="catalogue-group-label">{activeGroup.label}</p>
              <p className="catalogue-group-description">{activeGroup.description}</p>
            </section>
          )}

          <div className="catalogue-grid">
            {(activeGroup?.items || []).map((item, index) => (
            <article
              className={`catalogue-card ${index % 3 === 1 ? "catalogue-card-dark" : ""}`}
              key={`prestation_${item.id}`}
            >
              <h3>{item.label}</h3>

              <div className="catalogue-card-line" />

              <p className="catalogue-card-plan">
                <span>{formatDuration(item.duration)}</span>
                <em>/</em>
                <span>{formatPrice(item.price)}</span>
              </p>

              <div className="catalogue-card-content">
                <p className="catalogue-card-description">
                  {item.description || "Soin personnalise en institut."}
                </p>
              </div>

              <button
                type="button"
                className="catalogue-card-btn"
                onClick={() => navigate(`/public/catalogue/${item.id}`)}
              >
                Voir le detail
              </button>
              <button
                type="button"
                className="catalogue-card-btn catalogue-card-btn-rdv"
                onClick={() => navigate(`/public/rendez-vous/${item.id}`)}
                disabled={!!item.isComplementOnly}
              >
                {item.isComplementOnly ? "Complément non réservable seul" : "Prendre rendez-vous"}
              </button>
            </article>
          ))}
          </div>
        </>
      )}
    </section>
  );
}

export default Catalogue;
